import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { apiClient } from 'config/api';
import { useCustomerId } from '@hooks';

interface UseDownloadMyDispersionsResponse {
  isLoading: boolean;
  download: (params: Record<string, string>) => Promise<void>;
  error: string | undefined;
}

/**
 * Get header value ignoring case
 * @param headers - Response headers object
 * @param key - Header key to look for
 * @returns Header value if found
 */
const getHeaderValue = (headers: Record<string, unknown>, key: string): string | undefined => {
  const originalKey = Object.keys(headers).find((k) => k.toLowerCase() === key.toLowerCase());
  const value = originalKey ? headers[originalKey] : undefined;
  return typeof value === 'string' ? value : undefined;
};

/**
 * Download my dispersions list as xlsx using current search filters
 */
const useDownloadMyDispersions = (): UseDownloadMyDispersionsResponse => {
  const { customerId } = useCustomerId();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const download = useCallback(
    async (params: Record<string, string>) => {
      setIsLoading(true);
      setError(undefined);
      try {
        const queryParams: Record<string, string> = {
          ...(params.fromDate && { fromDate: params.fromDate }),
          ...(params.toDate && { toDate: params.toDate }),
          ...(params.scheduleType &&
            params.scheduleType !== 'all' && { scheduleType: params.scheduleType }),
          ...(params.status && { status: params.status }),
          ...(params.areaId && { areaId: params.areaId }),
        };
        const query = new URLSearchParams(queryParams).toString();
        const path = customerId
          ? `/v3/export/dispersion/search/${customerId}`
          : '/v3/export/dispersion/search';

        const { headers, data } = await apiClient.get(`${path}?${query}`, {
          responseType: 'blob',
        });

        const contentDisposition = getHeaderValue(
          headers as Record<string, unknown>,
          'content-disposition'
        );
        const contentType =
          getHeaderValue(headers as Record<string, unknown>, 'content-type') ??
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const filenameMatch = contentDisposition && contentDisposition.match(/filename=([^;]+)/);
        const filename = filenameMatch
          ? filenameMatch[1].replace(/"/g, '')
          : `${t('DISPERSIONS:MY_DISPERSIONS')}.xlsx`;

        const blob = new Blob([data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        if (isAxiosError(err)) {
          setError(err.message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [customerId, t]
  );

  return { isLoading, download, error };
};

export default useDownloadMyDispersions;
